import { useEffect, useState } from "react";
import { getMentor, upsertMentorProfile, listMentors, createMentoringEngagement, fetchMentoringEngagements, fetchHrMentorDashboard, closeMentoringEngagement } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { toast } from "sonner";
import { UserCheck, Users, Save, Loader2, X, Sparkles, Award } from "lucide-react";

/** ===== 1) MentorProfileEditor — Employee sets themselves as mentor ===== */
export function MentorProfileEditor({ employeeId }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState("");

  useEffect(() => {
    getMentor(employeeId).then(setProfile).catch(() => setProfile({ is_mentor: false, expertise_areas: [], mentee_capacity: 3, preferred_duration_months: 6, preferred_mentee_level: "all", bio: "" }));
  }, [employeeId]);

  if (!profile) return <div className="card-surface p-6 text-sm text-slate-400">Yükleniyor…</div>;

  const addSkill = () => { if (newSkill.trim() && !(profile.expertise_areas || []).includes(newSkill.trim())) { setProfile({ ...profile, expertise_areas: [...(profile.expertise_areas || []), newSkill.trim()] }); setNewSkill(""); } };
  const removeSkill = (s) => setProfile({ ...profile, expertise_areas: (profile.expertise_areas || []).filter((x) => x !== s) });

  const save = async () => {
    setSaving(true);
    try {
      await upsertMentorProfile(employeeId, {
        is_mentor: !!profile.is_mentor,
        expertise_areas: profile.expertise_areas || [],
        mentee_capacity: profile.mentee_capacity || 3,
        preferred_duration_months: profile.preferred_duration_months || 6,
        preferred_mentee_level: profile.preferred_mentee_level || "all",
        bio: profile.bio || "",
      });
      toast.success(profile.is_mentor ? "Mentor profilin güncellendi" : "Mentor olarak devre dışı bırakıldın");
    } catch { toast.error("Kaydedilemedi"); } finally { setSaving(false); }
  };

  return (
    <div className="card-surface p-6 space-y-4" data-testid="mentor-profile-editor">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5" /> Mentor Profilim</div>
          <h3 className="font-display text-xl font-semibold">Mentorluk Kayıt</h3>
          <p className="text-xs text-slate-500 mt-1">Aktif olduğunda çalışanlar seni mentor havuzunda arayabilir.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="mentor-toggle">
          <input type="checkbox" checked={!!profile.is_mentor} onChange={(e) => setProfile({ ...profile, is_mentor: e.target.checked })} className="w-4 h-4 accent-emerald-500" />
          <span className={profile.is_mentor ? "text-emerald-700 font-semibold" : "text-slate-500"}>{profile.is_mentor ? "Aktif Mentor" : "Pasif"}</span>
        </label>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">Uzmanlık alanların</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(profile.expertise_areas || []).map((s) => (
            <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs" data-testid={`mentor-skill-${s}`}>
              {s}
              <button onClick={() => removeSkill(s)} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input placeholder="Ekle (örn: Liderlik, Python, Süreç yönetimi)" value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSkill()} data-testid="mentor-skill-input" />
          <Button variant="outline" onClick={addSkill}>+ Ekle</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Mentee kapasitesi</label>
          <Input type="number" min={1} max={10} value={profile.mentee_capacity || 3} onChange={(e) => setProfile({ ...profile, mentee_capacity: parseInt(e.target.value) || 3 })} data-testid="mentor-capacity" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Tercih ettiğin süre</label>
          <Select value={String(profile.preferred_duration_months || 6)} onValueChange={(v) => setProfile({ ...profile, preferred_duration_months: parseInt(v) })}>
            <SelectTrigger className="bg-white" data-testid="mentor-duration"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="3">3 ay</SelectItem>
              <SelectItem value="6">6 ay</SelectItem>
              <SelectItem value="12">12 ay</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Tercih ettiğin mentee seviyesi</label>
          <Select value={profile.preferred_mentee_level || "all"} onValueChange={(v) => setProfile({ ...profile, preferred_mentee_level: v })}>
            <SelectTrigger className="bg-white" data-testid="mentor-level"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Herkes</SelectItem>
              <SelectItem value="junior">Junior</SelectItem>
              <SelectItem value="mid">Mid</SelectItem>
              <SelectItem value="senior">Senior</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">Kısa bio / motivasyon</label>
        <Textarea rows={2} value={profile.bio || ""} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Örn: 10+ yıl deneyimli veri bilimci; kariyer geçişi olanlara destek olmayı seviyorum." data-testid="mentor-bio" />
      </div>

      {profile.is_mentor && (
        <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-xs text-emerald-800 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Aktif kapasite: <span className="font-bold">{profile.available_slots ?? 0}/{profile.mentee_capacity}</span> · Aktif mentee: <span className="font-bold">{profile.active_mentees ?? 0}</span>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={saving} data-testid="mentor-profile-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Profili Kaydet
        </Button>
      </div>
    </div>
  );
}

/** ===== 2) MentorDiscovery — Employees browse available mentors and request engagement ===== */
export function MentorDiscovery({ employeeId }) {
  const [mentors, setMentors] = useState([]);
  const [q, setQ] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listMentors({ only_available: onlyAvailable }).then((d) => setMentors(d.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [onlyAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = q ? mentors.filter((m) => (m.employee_name + " " + (m.expertise_areas || []).join(" ")).toLowerCase().includes(q.toLowerCase())) : mentors;

  return (
    <div className="card-surface p-6" data-testid="mentor-discovery">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Mentor Havuzu</div>
          <h3 className="font-display text-xl font-semibold">Bir Mentor Bul</h3>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} className="w-3.5 h-3.5" data-testid="mentor-only-available" />
          Sadece boşluğu olanlar
        </label>
      </div>
      <Input placeholder="Uzmanlık ara: Python, Liderlik…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4" data-testid="mentor-search" />

      {loading ? <div className="text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Yükleniyor…</div> : filtered.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">Uygun mentor bulunamadı.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((m) => (
            <div key={m.employee_id} className="p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`mentor-card-${m.employee_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-11 h-11">
                  <AvatarImage src={m.employee_avatar} />
                  <AvatarFallback>{(m.employee_name || "?").slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{m.employee_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{m.employee_role} · {m.employee_department}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(m.expertise_areas || []).slice(0, 4).map((s) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{s}</span>
                    ))}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${m.available_slots > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {m.available_slots}/{m.mentee_capacity} slot
                </span>
              </div>
              {m.bio && <div className="text-[11px] text-slate-600 mt-2 italic line-clamp-2">&ldquo;{m.bio}&rdquo;</div>}
              <div className="flex items-center justify-between mt-3 text-[11px] text-slate-500">
                <span>Tercih süresi: <span className="font-medium text-slate-700">{m.preferred_duration_months} ay</span></span>
                <Button size="sm" disabled={m.available_slots <= 0 || m.employee_id === employeeId} onClick={() => setSelected(m)} className="h-7 text-xs" data-testid={`mentor-request-${m.employee_id}`}>
                  {m.employee_id === employeeId ? "Bu sensin" : m.available_slots > 0 ? "Mentor İste" : "Slot yok"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <EngagementDialog mentor={selected} menteeId={employeeId} onClose={() => setSelected(null)} onCreated={() => { setSelected(null); reload(); }} />
      )}
    </div>
  );
}

function EngagementDialog({ mentor, menteeId, onClose, onCreated }) {
  const [goals, setGoals] = useState("");
  const [duration, setDuration] = useState(mentor.preferred_duration_months || 6);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await createMentoringEngagement({ mentor_id: mentor.employee_id, mentee_id: menteeId, goals, duration_months: duration });
      toast.success("Mentorluk başlatıldı!");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white" data-testid="engagement-dialog">
        <DialogHeader>
          <DialogTitle>{mentor.employee_name} ile Mentorluk</DialogTitle>
          <DialogDescription>Hedeflerini ve süreni belirle; mentor talebin oluşturulacak.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Hedeflerin</label>
            <Textarea rows={3} placeholder="Bu mentorluk süresinde neyi başarmak istiyorsun?" value={goals} onChange={(e) => setGoals(e.target.value)} data-testid="engagement-goals" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Süre</label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v))}>
              <SelectTrigger className="bg-white" data-testid="engagement-duration"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="3">3 ay</SelectItem><SelectItem value="6">6 ay</SelectItem><SelectItem value="12">12 ay</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={busy} data-testid="engagement-start">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Başlat"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** ===== 3) MyMentoringEngagements — See engagements as mentor or mentee ===== */
export function MyMentoringEngagements({ employeeId }) {
  const [data, setData] = useState(null);
  const reload = () => fetchMentoringEngagements(employeeId).then(setData);
  useEffect(() => { reload(); }, [employeeId]);

  const closeEng = async (id) => {
    try { await closeMentoringEngagement(id, "completed"); toast.success("Kapatıldı"); reload(); }
    catch { toast.error("Kapatılamadı"); }
  };

  if (!data) return null;
  const total = (data.as_mentor?.length || 0) + (data.as_mentee?.length || 0);
  if (total === 0) return null;

  return (
    <div className="card-surface p-6 space-y-4" data-testid="my-engagements">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Mentorluk İlişkilerim</div>
        <h3 className="font-display text-xl font-semibold">Aktif Kayıtlar</h3>
      </div>
      {data.as_mentor?.length > 0 && (
        <div>
          <div className="text-xs uppercase font-semibold text-emerald-700 mb-1">Mentor olarak</div>
          <div className="space-y-1.5">
            {data.as_mentor.map((e) => <EngRow key={e.id} eng={e} otherName={e.mentee_name} role="mentor" onClose={closeEng} />)}
          </div>
        </div>
      )}
      {data.as_mentee?.length > 0 && (
        <div>
          <div className="text-xs uppercase font-semibold text-sky-700 mb-1">Mentee olarak</div>
          <div className="space-y-1.5">
            {data.as_mentee.map((e) => <EngRow key={e.id} eng={e} otherName={e.mentor_name} role="mentee" onClose={closeEng} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function EngRow({ eng, otherName, role, onClose }) {
  const active = eng.status === "active";
  const endsAt = eng.ends_at ? new Date(eng.ends_at).toLocaleDateString("tr-TR") : "-";
  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-testid={`engagement-row-${eng.id}`}>
      <div className="text-sm">
        <div className="font-medium">{otherName}</div>
        <div className="text-[11px] text-slate-500">{eng.duration_months} ay · Bitiş: {endsAt}</div>
        {eng.goals && <div className="text-[11px] text-slate-600 italic mt-0.5">&ldquo;{eng.goals}&rdquo;</div>}
      </div>
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{active ? "Aktif" : "Kapalı"}</span>
        {active && role === "mentor" && (
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onClose(eng.id)} data-testid={`engagement-close-${eng.id}`}>Kapat</Button>
        )}
      </div>
    </div>
  );
}

/** ===== 4) HrMentorDashboard — HR overview ===== */
export function HrMentorDashboard() {
  const [dash, setDash] = useState(null);
  useEffect(() => { fetchHrMentorDashboard().then(setDash); }, []);
  if (!dash) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hr-mentor-dashboard">
      <Stat label="Aktif Mentor" value={dash.total_mentors} color="emerald" />
      <Stat label="Toplam Kapasite" value={dash.total_capacity} color="sky" />
      <Stat label="Aktif İlişki" value={dash.total_active_engagements} color="amber" />
      <Stat label="Doluluk" value={`%${dash.utilization_pct}`} color="indigo" />
      {dash.ending_soon?.length > 0 && (
        <div className="col-span-2 md:col-span-4 card-surface p-4">
          <div className="text-xs uppercase font-semibold text-slate-600 mb-2">45 gün içinde biten ilişkiler ({dash.ending_soon.length})</div>
          <div className="space-y-1">
            {dash.ending_soon.slice(0, 8).map((d) => (
              <div key={d.id} className="text-sm flex items-center justify-between">
                <span>{d.mentor_name} ↔ {d.mentee_name}</span>
                <span className="text-[11px] text-slate-500">Bitiş: {new Date(d.ends_at).toLocaleDateString("tr-TR")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const cmap = { emerald: "bg-emerald-50 text-emerald-800 border-emerald-200", sky: "bg-sky-50 text-sky-800 border-sky-200", amber: "bg-amber-50 text-amber-800 border-amber-200", indigo: "bg-indigo-50 text-indigo-800 border-indigo-200" };
  return (
    <div className={`rounded-xl border p-3 ${cmap[color] || cmap.sky}`}>
      <div className="text-2xl font-display font-bold">{value}</div>
      <div className="text-[11px] uppercase tracking-wider">{label}</div>
    </div>
  );
}
